import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PlaylistBrief, PlaylistFolder } from '../utils/redux/playlistSlice'
import { apiBaseURL } from '../utils/api'
import { getAccentById, getGradientById } from '.././utils/color'
import './PlaylistSection.scss'

interface PlaylistSectionProps {
  title: string
  items: PlaylistBrief[] | PlaylistFolder[]
  type: 'playlist' | 'mood' | 'style' | 'daytime' | 'business'
}

export default function PlaylistSection({ title, items, type }: PlaylistSectionProps) {
  const navigate = useNavigate()
  const { i18n } = useTranslation()

  const trackRef = useRef<HTMLDivElement | null>(null)
  const [canLeft, setCanLeft] = useState(false)
  const [canRight, setCanRight] = useState(false)

  const isMood = type === 'mood'
  const isTiles = type === 'style' // tiles только для styles

  // ===== helpers: текст из локализованных объектов =====
  const asText = (value: any): string => {
    if (value == null) return ''
    if (typeof value === 'string' || typeof value === 'number') return String(value)
    if (Array.isArray(value)) return value.map(asText).filter(Boolean).join(' ')

    if (typeof value === 'object') {
      const lang = (i18n.language || 'en').split('-')[0]
      if (value[lang]) return asText(value[lang])
      if (value.en) return asText(value.en)
      if (value.ru) return asText(value.ru)
      if (value.fr) return asText(value.fr)

      const firstString = Object.values(value).find((v) => typeof v === 'string')
      return firstString ? String(firstString) : ''
    }

    return ''
  }

  const getTitleText = (item: any) => asText(item?.title ?? item?.name)
  const getSubtitleText = (item: any) =>
    asText(
      item?.subtitle ??
        item?.description ??
        (type === 'playlist' ? 'Made for you' : 'Category'),
    )

  // ===== routing logic =====
  const handleItemClick = (item: PlaylistBrief | PlaylistFolder) => {
    if (type === 'playlist') navigate(`/playlist/${item._id}`)
    else navigate(`/category/${type}/${item._id}`)
  }

  const handleMoreClick = () => {
    if (type === 'playlist') navigate('/playlists')
    else navigate(`/category/${type}`)
  }

  const getImageUrl = (item: PlaylistBrief | PlaylistFolder) => {
    if (type === 'playlist') return `${apiBaseURL}/uploaded/playlists/${item.image}`
    if (type === 'business') return `${apiBaseURL}/uploaded/businesses/${item.image}`
    return `${apiBaseURL}/uploaded/${type}s/${item.image}`
  }

  // ===== slider logic: ТОЛЬКО если не mood =====
  const GAP = 22 // должен совпадать с gap в css

  const calcStep = () => {
    const el = trackRef.current
    if (!el) return 320

    const card = el.querySelector<HTMLElement>('.ps-card, .ps-tile')
    if (!card) return Math.floor(el.clientWidth * 0.8)

    return card.offsetWidth + GAP
  }

  const scrollByDir = (dir: -1 | 1) => {
    const el = trackRef.current
    if (!el) return
    el.scrollBy({ left: dir * calcStep(), behavior: 'smooth' })
  }

  const updateArrows = () => {
    const el = trackRef.current
    if (!el) return
    const left = el.scrollLeft
    const max = el.scrollWidth - el.clientWidth
    setCanLeft(left > 2)
    setCanRight(left < max - 2)
  }

  useEffect(() => {
    if (isMood) return

    updateArrows()
    const el = trackRef.current
    if (!el) return

    const onScroll = () => updateArrows()
    el.addEventListener('scroll', onScroll, { passive: true })

    const onResize = () => updateArrows()
    window.addEventListener('resize', onResize)

    return () => {
      el.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
    }
  }, [items, isMood])

  const safeItems = useMemo(() => items ?? [], [items])

  return (
    <section
      className={`ps ${isMood ? 'ps--mood' : isTiles ? 'ps--tiles' : 'ps--playlist'}`}
    >
      <div className="ps__head">
        <h2 className="ps__title">{title}</h2>

        {!isMood && (
          <div className="ps__controls">
            <button type="button" className="ps__more" onClick={handleMoreClick}>
              Ещё
            </button>

            <button
              type="button"
              className="ps__arrowBtn"
              onClick={() => scrollByDir(-1)}
              disabled={!canLeft}
              aria-label="scroll left"
            >
              ‹
            </button>

            <button
              type="button"
              className="ps__arrowBtn"
              onClick={() => scrollByDir(1)}
              disabled={!canRight}
              aria-label="scroll right"
            >
              ›
            </button>
          </div>
        )}
      </div>

      {/* ✅ MOOD: grid */}
      {isMood ? (
        <div className="ps-moodGrid">
          {safeItems.map((item: any) => {
            const titleText = getTitleText(item)
            const id = String(item?._id ?? titleText)
            const acc = getAccentById(id, 'mood')

            return (
              <button
                key={id}
                type="button"
                className="ps-pill"
                style={{ ['--acc' as any]: acc } as React.CSSProperties}
                onClick={() => handleItemClick(item)}
                title={titleText}
              >
                {titleText}
              </button>
            )
          })}
        </div>
      ) : (
        <div className="ps__track" ref={trackRef}>
          {safeItems.map((item: any) => {
            const titleText = getTitleText(item)
            const subText = getSubtitleText(item)
            const id = String(item?._id ?? titleText)

            // cards (playlist + всё кроме style)
            if (!isTiles) {
              // если хочешь — можешь использовать градиент как фон под картинкой/плейсхолдер
              const grad = getGradientById(id, type)

              return (
                <button
                  key={id}
                  type="button"
                  className="ps-card"
                  onClick={() => handleItemClick(item)}
                  title={titleText}
                  style={
                    {
                      ['--card-a' as any]: grad.a,
                      ['--card-b' as any]: grad.b,
                    } as React.CSSProperties
                  }
                >
                  <div className="ps-card__imgWrap">
                    <img
                      className="ps-card__img"
                      src={getImageUrl(item)}
                      alt={titleText}
                      loading="lazy"
                    />
                  </div>

                  <div className="ps-card__text">
                    <div className="ps-card__name">{titleText}</div>
                    <div className="ps-card__sub">{subText}</div>
                  </div>
                </button>
              )
            }

            // tiles (style)
            const grad = getGradientById(id, 'style')

            return (
              <button
                key={id}
                type="button"
                className="ps-tile"
                onClick={() => handleItemClick(item)}
                title={titleText}
                style={
                  {
                    ['--tile-a' as any]: grad.a,
                    ['--tile-b' as any]: grad.b,
                  } as React.CSSProperties
                }
              >
                <div className="ps-tile__label">{titleText}</div>

                <div className="ps-tile__artWrap">
                  <img
                    className="ps-tile__art"
                    src={getImageUrl(item)}
                    alt={titleText}
                    loading="lazy"
                  />
                </div>
              </button>
            )
          })}
        </div>
      )}
    </section>
  )
}
